import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, LinearProgress } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { RadioButtonGroup, TextFieldElement } from 'react-hook-form-mui';
import * as yup from 'yup';
import { useAuthContext } from '../../../../../../context/auth';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_PLACEHOLDER,
	FORM_MESSAGE,
} from '../../../../../fastForm/data/constants';
import { DateField } from '../../../../../fastForm/fields';
import { dateConversion } from '../../../../../fastForm/utils/dateConversion';
import { FileUploadField } from '../../../../../hookFormFields';
import StaticDocumentsAPI, {
	AddStaticDocumentRequest,
} from '../../../api/staticDocuments';
import {
	MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
	MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
	STATIC_DOCS_MODAL_CANCEL_BUTTON,
	STATIC_DOCS_MODAL_EFFECTIVE_DATE_FIELD,
	STATIC_DOCS_MODAL_FAILURE_MESSAGE,
	STATIC_DOCS_MODAL_FILE_FIELD,
	STATIC_DOCS_MODAL_FILE_NAME_FIELD,
	STATIC_DOCS_MODAL_FILE_TYPE_FIELD,
	STATIC_DOCS_MODAL_FINISHED_BUTTON,
	STATIC_DOCS_MODAL_LOADING_MESSAGE,
	STATIC_DOCS_MODAL_SUBMIT_BUTTON,
	STATIC_DOCS_MODAL_SUCCESS_MESSAGE,
	STATIC_DOCS_MODAL_UPLOAD_MORE_BUTTON,
} from '../../../data/constants';
import { TABLE_KEY, useFilterParams } from '../StaticDocuments';

const DATE_PICKER_PRECISION = 'hour';

interface StaticDocumentFormData {
	file: FileList;
	fileName: string;
	effectiveDate: string;
	fileType: 'zip' | 'pdf';
}

interface AddStaticDocumentFormProps {
	tenantHostname?: string;
	handleClose: () => void;
}

const AddStaticDocumentForm = (props: AddStaticDocumentFormProps) => {
	const authData = useAuthContext();
	const timezone = authData?.auth?.timezone;
	const { asOfDate, fileName } = useFilterParams();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { handleClose, tenantHostname } = props;
	const [successState, setSuccessState] = React.useState(false);

	const {
		mutateAsync: addStaticDocument,
		isLoading,
		data,
	} = StaticDocumentsAPI.useAddStaticDocumentMutation(
		[TABLE_KEY, tenantId || tenantHostname, asOfDate, fileName],
		authToken,
		tenantHostname,
	);

	const { handleSubmit, control, register, watch, reset } =
		useForm<StaticDocumentFormData>({
			resolver: yupResolver(
				yup.object().shape({
					effectiveDate: yup
						.string()
						.required(FORM_MESSAGE.required)
						.test(
							'is-valid-date',
							DATE_FIELD_VALIDATIONS.INVALID_FORMAT_ERROR_MESSAGE +
								DATE_PRECISION_PLACEHOLDER[DATE_PICKER_PRECISION],
							(value) => {
								const date = dateConversion.dateFormStringToDayJs(
									value,
									DATE_PICKER_PRECISION,
								);
								return date?.isValid();
							},
						),
					fileName: yup.string().when('fileType', {
						is: 'pdf',
						then: yup.string().required(FORM_MESSAGE.required),
					}),
					file: yup.mixed().test({
						test: (value) => {
							if (!value || value.length === 0 || !value[0]?.name) {
								return false;
							} else {
								return true;
							}
						},
						message: FORM_MESSAGE.required,
					}),
				}),
			),
			mode: 'onChange',
			defaultValues: {
				fileName: '',
				file: null,
				// default to 12:00 AM of current day
				effectiveDate: dayjs()
					.hour(0)
					.minute(0)
					.second(0)
					.format(DATE_PRECISION_INPUT_FORMAT[DATE_PICKER_PRECISION]),
				fileType: 'zip',
			},
		});

	const fileType = watch('fileType');

	const onSubmit: SubmitHandler<StaticDocumentFormData> = async (data) => {
		const request = {} as AddStaticDocumentRequest;

		request.file = data.file[0];
		if (data.fileName) {
			request.fileName = data.fileName;
			// if file does not have .pdf extension, add it
			const extension = request.fileName.split('.')?.pop();
			if (extension !== 'pdf') {
				request.fileName = `${request.fileName}.pdf`;
			}
		}
		if (data.effectiveDate) {
			request.asOfTimestamp = parseInt(
				dateConversion.convertDateStringToZonedTimestamp(
					data.effectiveDate,
					DATE_PICKER_PRECISION,
					timezone,
				),
			);
		}

		await addStaticDocument(request);

		setSuccessState(true);
	};

	const showFormInputs = !isLoading && !successState;

	return (
		<Box
			onSubmit={handleSubmit(onSubmit)}
			component='form'
			sx={{ pt: 1, pb: 2 }}
		>
			<Collapse in={isLoading}>
				<Stack sx={{ width: '100%' }} spacing={2}>
					{STATIC_DOCS_MODAL_LOADING_MESSAGE()}
					<LinearProgress />
				</Stack>
			</Collapse>
			<Collapse in={successState}>
				<Stack spacing={2}>
					{data?.successfulDocuments.length > 0 && (
						<Alert
							icon={false}
							security='success'
							sx={{
								alignItems: 'flex-start',
								width: '100%',
								'&.MuiAlert-message': {
									width: '100%',
								},
							}}
						>
							<Accordion
								disableGutters
								sx={{
									backgroundColor: 'transparent',
									border: 'none',
									boxShadow: 'none',
									width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls='success-documents-list'
								>
									<Stack spacing={2} direction='row'>
										<CheckCircleIcon color='inherit' />
										<Typography>
											{data?.successfulDocuments.length +
												' ' +
												STATIC_DOCS_MODAL_SUCCESS_MESSAGE(
													data?.successfulDocuments.length > 1,
												)}
										</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<Box
										sx={{
											maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
											overflowX: 'hidden',
										}}
									>
										<Stack spacing={1}>
											{data?.successfulDocuments.map((success) => (
												<Typography key={success.fileName} variant='caption'>
													{success.fileName}
												</Typography>
											))}
										</Stack>
									</Box>
								</AccordionDetails>
							</Accordion>
						</Alert>
					)}
					{data?.failedDocuments.length > 0 && (
						<Alert
							icon={false}
							severity='error'
							sx={{
								alignItems: 'flex-start',
								width: '100%',
								'&.MuiAlert-message': {
									width: '100%',
								},
							}}
						>
							<Accordion
								disableGutters
								sx={{
									backgroundColor: 'transparent',
									border: 'none',
									boxShadow: 'none',
									width: MODULAR_CONFIG_UPLOAD_ALERT_WIDTH,
								}}
							>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									aria-controls='failed-documents-list'
								>
									<Stack spacing={2} direction='row'>
										<ErrorIcon color='inherit' />
										<Typography>
											{data?.failedDocuments.length +
												' ' +
												STATIC_DOCS_MODAL_FAILURE_MESSAGE(
													data?.failedDocuments.length > 1,
												)}
										</Typography>
									</Stack>
								</AccordionSummary>
								<AccordionDetails>
									<Box
										sx={{
											maxHeight: MODULAR_CONFIG_UPLOAD_ALERT_HEIGHT,
											overflowY: 'scroll',
											overflowX: 'hidden',
										}}
									>
										<Stack spacing={1}>
											{data?.failedDocuments.map((failure) => (
												<Typography key={failure.fileName} variant='caption'>
													{failure.errorDetails}
												</Typography>
											))}
										</Stack>
									</Box>
								</AccordionDetails>
							</Accordion>
						</Alert>
					)}
				</Stack>
			</Collapse>
			<Collapse in={showFormInputs}>
				<Stack spacing={2}>
					<RadioButtonGroup
						control={control}
						row
						label={STATIC_DOCS_MODAL_FILE_TYPE_FIELD()}
						name='fileType'
						options={[
							{
								id: 'zip',
								label: 'Zip File',
							},
							{
								id: 'pdf',
								label: 'PDF File',
							},
						]}
					/>
					<FileUploadField
						sx={{ mt: 2 }}
						label={STATIC_DOCS_MODAL_FILE_FIELD()}
						name='file'
						accept={fileType === 'pdf' ? '.pdf' : 'application/zip'}
						disabled={isLoading}
						control={control}
						register={register}
					/>
					<Collapse in={fileType === 'pdf'}>
						<TextFieldElement
							sx={{ mt: 1, mb: 1 }}
							name='fileName'
							label={STATIC_DOCS_MODAL_FILE_NAME_FIELD()}
							variant='outlined'
							control={control}
							disabled={isLoading}
							fullWidth
						/>
					</Collapse>
					<DateField<StaticDocumentFormData>
						name='effectiveDate'
						title={STATIC_DOCS_MODAL_EFFECTIVE_DATE_FIELD()}
						control={control}
						disabled={isLoading}
						defaultToMidnight={true}
						precision={DATE_PICKER_PRECISION}
						authData={authData}
						order={0}
						values={[]}
						disableTimePicker={false}
						showCalendar
						type='date'
						variant='outlined'
					/>
				</Stack>
			</Collapse>
			<Stack
				direction='row'
				spacing={2}
				justifyContent='flex-end'
				sx={{ mt: 2 }}
			>
				{!successState && (
					<Button onClick={handleClose} color='inherit' disabled={isLoading}>
						{STATIC_DOCS_MODAL_CANCEL_BUTTON()}
					</Button>
				)}
				{!successState && (
					<Button
						type='submit'
						variant='contained'
						color='secondary'
						disabled={isLoading}
					>
						{STATIC_DOCS_MODAL_SUBMIT_BUTTON()}
					</Button>
				)}
				{successState && (
					<Button
						onClick={handleClose}
						color='neutralButton'
						disabled={isLoading}
					>
						{STATIC_DOCS_MODAL_FINISHED_BUTTON()}
					</Button>
				)}
				{successState && (
					<Button
						type='button'
						variant='contained'
						color='secondary'
						disabled={isLoading}
						onClick={() => {
							setSuccessState(false);
							reset();
						}}
					>
						{STATIC_DOCS_MODAL_UPLOAD_MORE_BUTTON()}
					</Button>
				)}
			</Stack>
		</Box>
	);
};

export default AddStaticDocumentForm;
